/**
 * --------------------------------------------------------------------------
 * Hive Component: Extensions/FormHandler.js
 * Copyright (c) 2005 - 2022 Paul Höcker / PHMG All rights reserved.
 * Licensed under GNU General Public License version 2 or later
 * --------------------------------------------------------------------------
 */

// noinspection JSUnusedGlobalSymbols
//import LoadingDisplay from "@phmg/apis/src/js/LoadingDisplay.js";

/**
 * Constants
 */

const NAME = 'FormHandler'
const VERSION = '1.2.2'

let utils, loadingDisplay;

/**
 * FormHandler
 */
export default class FormHandler {
	/**
	 * @type {Controller}
	 */
	Controller = null;

	/**
	 * @type {HTMLFormElement}
	 */
	_formElement = null;

	_formContext = '';

	//noinspection JSValidateJSDoc
	/**
	 * @type {JFormValidator|*}
	 */
	Validator = null;
	ValidationHandlers = {};

	//noinspection JSUnusedGlobalSymbols
	//EditorIDs = [];
	Editors = {};

	/**
	 * @return {string}
	 */
	static get NAME() {
		return NAME
	}

	/**
	 * @return {string}
	 */
	static get VERSION() {
		return VERSION
	}

	/**
	 *
	 * @param {Controller|*} controller
	 * @param {HTMLFormElement|string} form
	 */
	constructor(controller, form) {
		utils = Hive.getUtils();
		loadingDisplay = Hive.getLoadingDisplay();

		try {
			this.Controller = controller || null;
			this._formElement = typeof form == "string" ? document.querySelector(form) : form;
			if (!this._formElement) {
				utils.error('FormHandler: form not found')
				return;
			}

			this._formContext = this._formElement.getAttribute('data-context') || '';
		} catch (exception) {
			utils.error(exception.message || 'ERROR');
		}
	}

	/**
	 * Get the FormData object
	 * @returns {FormData}
	 */
	getData() {
		return new FormData(this._formElement);
	}

	/**
	 * Get the form data as an object
	 * @returns {{}}
	 */
	getObject() {
		const formData = this.getData();
		const uniqueKeys = [...new Set(formData.keys())]
		const formObj = {}
		uniqueKeys.forEach((value) => {
			formObj[value] = (formData.getAll(value).length > 1) ? formData.getAll(value) : formData.get(value)
		})
		return formObj;
		// const json = JSON.stringify(formObj)
		// alert(json)
	}

	_eventListenersCount = 0
	_eventListeners = {}

	/**
	 *
	 * @param {string} selector
	 * @param {string|string[]} eventName
	 * @param {EventListenerOrEventListenerObject} listener
	 * @param {string} [listenerId]
	 * @returns {FormHandler}
	 */
	addListener(eventName, selector, listener, listenerId) {
		let count = this._eventListenersCount++;
		listenerId = listenerId || `el.${count}`
		this._eventListeners[listenerId] = (event) => {
			if (event.target.matches(selector)) {
				listener(event)
			}
		}
		if (Array.isArray(eventName)) {
			eventName.forEach(evName => this._formElement.addEventListener(evName, this._eventListeners[listenerId]))
		} else {
			this._formElement.addEventListener(eventName, this._eventListeners[listenerId]);
		}

		return this;
	}

	/**
	 * Track changes
	 * @param {function} callback
	 * @param {number} [timeout]
	 */
	track(callback, timeout) {
		const self = this;
		const timerId = this._formElement.id + '.input-update';
		// selectors = selectors || 'select,input[type=checkbox],input[type=radio],input[type=text],textarea';
		timeout = timeout || 1000;

		this.addListener('change',
			'select,input[type=checkbox],input[type=radio],input[type=text],textarea',
			(event) => {
				utils.clearTimer(timerId);
				utils.addTimer(() => {
					callback(self.getTrackData(), event);
				}, timeout, timerId)
			})

		this.addListener('keyup',
			'input[type=text],textarea',
			(event) => {
				utils.clearTimer(timerId);
				utils.addTimer(() => {
					callback(self.getTrackData(), event);
				}, timeout, timerId)
			})

		utils.addTimer(() => {
			callback(self.getTrackData(), {});
		}, timeout, timerId)
	}

	/**
	 *
	 * @return {FormData}
	 */
	getTrackData() {
		const formData = this.getData();
		let formTask = formData.get('task'),
			formOption = formData.get('option');
		formData.delete('task');
		formData.delete('option');

		let context = this._formContext.length ? this._formContext : (formOption ? formOption + '.' : '') + formTask;
		formData.append('context', context);

		return formData;
	}

	/**
	 * Deactivate all buttons
	 * @return {FormHandler}
	 */
	deactivate() {
		let buttons = [].slice.call(this._formElement.querySelectorAll('button:enabled'));
		buttons.forEach((button) => {
			button.disabled = true;
			button.setAttribute('data-disabled-button', '');
		})
		return this;
	}

	/**
	 * Reactivate disabled buttons
	 * @return {FormHandler}
	 */
	reactivate() {
		let buttons = [].slice.call(this._formElement.querySelectorAll('button[data-disabled-button]'));
		buttons.forEach((button) => {
			button.disabled = false;
			button.removeAttribute('data-disabled-button');
		})
		return this;
	}

	/**
	 * Add a loading display and deactivate the form
	 * @returns {FormHandler}
	 */
	lockForm() {
		const ld = loadingDisplay.add(this._formElement);
		ld.display();

		this.deactivate();

		return this;
	}

	/**
	 * Handle success event
	 * @param success
	 * @returns {FormHandler}
	 */
	freeFrom(success) {
		if (success) {
			this.onSuccess();
		} else {
			this.onError();
		}

		return this;
	}

	/**
	 * Success callback
	 * @returns {FormHandler}
	 */
	onSuccess() {
		const ld = loadingDisplay.add(this._formElement);
		ld.success();

		this.reactivate();

		return this;
	}

	/**
	 * Error callback
	 * @returns {FormHandler}
	 */
	onError() {
		const ld = loadingDisplay.add(this._formElement);
		ld.error();

		this.reactivate();

		return this;
	}

	/**
	 * Set an error to a form element
	 * @param {string|HTMLElement} elementOrSelector
	 */
	static setError(elementOrSelector) {
		let errorElement;
		if (typeof elementOrSelector === 'string') {
			errorElement = document.querySelector(elementOrSelector)
		} else {
			errorElement = elementOrSelector;
		}

		if (errorElement) {
			let setFocus = errorElement;
			if (errorElement.matches('input,select,textarea')) {
				setFocus = errorElement.closest('label') || setFocus;
			}
			setFocus.focus({ focusVisible : true })
		}
		utils.grabMessage(null, null, 3000);
	}

	/**
	 * Toggle specific buttons
	 * @todo remove show class
	 * @param {string} selector
	 * @param {boolean|null} enable
	 * @returns {FormHandler}
	 */
	toggleButton(selector, enable) {
		enable = typeof enable === 'undefined' ? null : enable;
		if (enable !== null) selector += enable ? ':disabled' : ':enabled';

		const buttons = [].slice.call(this._formElement.querySelectorAll('button' + selector));
		buttons.forEach(button => {
			let isDisabled = button.disabled === true;
			let enableButton = enable === null ? isDisabled : enable;
			if (enableButton) {
				button.disabled = false;
				if (button.classList.contains('fade')) button.classList.add('show');
				button.removeAttribute('data-disabled-button');
			} else {
				button.disabled = true;
				if (button.classList.contains('fade')) button.classList.remove('show');
				button.setAttribute('data-disabled-button', '');
			}
		})

		return this;
	}

	/**
	 * Disable (or readonly) a group of radio inputs
	 * @param {string|HTMLElement} group
	 * @param {string|number|null} [value]
	 */
	disableInputGroup(group, value) {
		value = typeof value === 'undefined' || value === null ? null : value.toString();
		group = typeof group === 'string' ? this._formElement.querySelector(group) : group;
		if (!group) return;

		// Value null: disable all
		// Value set: disable all but this one (readonly)
		let checkedInput = group.querySelector('input:checked');
		let groupValue = checkedInput ? checkedInput.value : null;
		if (value === null && checkedInput) {
			checkedInput.checked = false;
			checkedInput.dispatchEvent(new Event('change', { bubbles : true }));
		} else if (value !== groupValue) {
			let toInput = group.querySelector(`input[value="${value}"]`);
			if (toInput) {
				toInput.disabled = false;
				toInput.checked = true;
				toInput.dispatchEvent(new Event('change', { bubbles : true }));
			}
		}

		// Disable all (unchecked) inputs
		[].slice.call(group.querySelectorAll('input:not(:checked)')).forEach(input => input.disabled = true);

		// Disable the fieldset
		if (value === null && group.tagName.toLowerCase() === 'fieldset') {
			group.disabled = true;
		}
	}

	/**
	 * Enable a group of radio inputs
	 * @param {string|HTMLElement} group
	 * @param {string|number|null} [value]
	 */
	enableInputGroup(group, value) {
		value = typeof value === 'undefined' || value === null ? null : value.toString();
		group = typeof group === 'string' ? this._formElement.querySelector(group) : group;
		if (!group) return;

		// Enable the fieldset
		if (group.tagName.toLowerCase() === 'fieldset' && group.disabled) {
			group.disabled = false;
		}

		// Enable all disabled inputs
		[].slice.call(group.querySelectorAll('input:disabled')).forEach(input => input.disabled = false);

		// Set the value
		let checkedInput = group.querySelector('input:checked');
		let groupValue = checkedInput ? checkedInput.value : null;
		if (value !== null && groupValue !== value) {
			let toInput = group.querySelector(`input[value="${value}"]`);
			if (toInput) {
				toInput.checked = true;
				toInput.dispatchEvent(new Event('change', { bubbles : true }));
			}
		}
	}

	/**
	 * Set required attribute
	 * @param {HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement} element
	 * @param {boolean} require
	 */
	setRequired(element, require) {
		require = typeof require === 'undefined' || require === null ? !element.required : require;
		if (require) {
			element.required = true;
			element.classList.add('required')
			element.removeAttribute('data-required-suppressed')
		} else {
			element.required = false;
			element.classList.remove('required')
			element.setAttribute('data-required-suppressed', '')
		}
	}

	/* ## Form Validation */

	//noinspection JSValidateJSDoc
	/**
	 * @return {JFormValidator}
	 */
	getValidator() {
		if (!this.Validator) this.Validator = document.formvalidator || null;

		return this.Validator;
	}

	/**
	 * Create validator methods
	 */
	initialiseValidator() {

		// Reactivate
		this.reactivate();

		// Still checking
		if (!this.getValidator()) return;

		/*
		Object.keys(this.customValidators).foreach(key => {
			if (this.customValidators[key].exec() !== true) {
				valid = false;
			}});
		*/

		// Equals
		this.ValidationHandlers['equals'] = (value, element) => {
			const selector = element.getAttribute('data-equals');
			if (selector) {
				const equals = document.querySelector(selector);
				return equals && equals.value !== '' && element.value !== '' && equals.value.toString() === element.value.toString();
			}
			return true;
		};

		// Dispatch before validate event
		this._formElement.dispatchEvent(new CustomEvent(utils.EVENT_KEYS.FORM_INIT_VALIDATOR,
			{
				bubbles : true,
				detail  : {
					handler   : this,
					validator : this.Validator
				}
			}))

		// Add to validator
		Object.keys(this.ValidationHandlers).forEach(key => {
			this.Validator.setHandler(key, this.ValidationHandlers[key]);
		})


	}

	/**
	 * Prevent form from submission
	 * @param {boolean} active
	 * @param {string|null} preventedBy Optional selector for an element/elements to focus on submit
	 * @returns {FormHandler}
	 */
	preventSubmit(active, preventedBy = null) {
		if (active) {
			this._formElement.classList.add('prevent-submit');
			this._formElement.setAttribute('data-prevent-submit', preventedBy || '');
		} else {
			this._formElement.classList.remove('prevent-submit');
			this._formElement.removeAttribute('data-prevent-submit');
		}

		return this;
	}

	/**
	 * Prepare forms for submission
	 */
	prepareSubmit() {
		const self = this;
		const form = this._formElement;

		// Find fields that need validation and add the class
		const validateFields = [].slice.call(form.querySelectorAll("[data-validate]"));
		validateFields.forEach(field => {
			field.getAttribute("data-validate").split(" ")
				.forEach(value => field.classList.add(`validate-${value}`))
		})

		// Make sure validator gets attached
		form.classList.add('form-validate', 'validate')

		// If document validator isn't loaded,
		// disable the form and add a new listener
		if (!this.getValidator()) {
			utils.addWindowLoadListener(() => self.initialiseValidator())
			this.deactivate();
		} else {
			this.initialiseValidator();
		}

		// Submit & reset buttons
		const buttons = [].slice.call(form.querySelectorAll('[data-submit-task]'));
		buttons.forEach(button => {
			button.addEventListener('click', event => {
				event.preventDefault();

				// Last call for the validator
				if (!this.getValidator()) {
					utils.warning('FormValidator nicht gefunden.');
				}

				// Dispatch before validate event
				form.dispatchEvent(new CustomEvent(utils.EVENT_KEYS.FORM_BEFORE_VALIDATE,
					{
						bubbles : true,
						detail  : {
							handler   : this,
							validator : this.Validator
						}
					}))

				const task = button.getAttribute('data-submit-task');
				const noValidate = button.hasAttribute('formnovalidate')
				const prevented = form.hasAttribute('data-prevent-submit')

				try {
					// Manually prevented
					if (prevented) {
						FormHandler.setError(form.getAttribute('data-prevent-submit') || '');
						this.preventSubmit(false);

						return;
					}

					// Validation
					let valid = noValidate || this.Validator.isValid(form);
					if (!valid) {
						form.classList.add('was-validated');
						let firstErrorElement = form.querySelector('.invalid');
						FormHandler.setError(firstErrorElement || '');
						this.preventSubmit(false);

						return;
					}

					// Lock the form
					this.lockForm();

					// And submit it
					this.submitform(task, form);

				} catch (exception) {
					utils.error(exception.message, exception);
				}

			});
		});
	}

	/**
	 * Generic submit form
	 *
	 * @param  {String}  task      The given task
	 * @param  {HTMLFormElement}    [form]      The form element
	 * @param  {boolean}   [validate]
	 *
	 * @returns  {void}
	 */
	submitform = (task, form, validate) => {
		let newForm = form || this._formElement;
		const newTask = task;

		if (newTask) {
			newForm.task.value = newTask;
		}

		// Toggle HTML5 validation
		newForm.noValidate = !validate;

		if (!validate) {
			newForm.setAttribute('novalidate', '');
		} else if (newForm.hasAttribute('novalidate')) {
			newForm.removeAttribute('novalidate');
		}

		// Submit the form.
		// Create the input type="submit"
		const button = document.createElement('input');
		button.classList.add('hidden');
		button.type = 'submit'; // Append it and click it
		newForm.appendChild(button).click(); // If "submit" was prevented, make sure we don't get a build up of buttons
		newForm.removeChild(button);
	}

	/**
	 * Get the form element
	 * @returns {HTMLFormElement}
	 */
	getForm() {
		return this._formElement;
	}

	//noinspection JSUnusedGlobalSymbols

	/**
	 * ## TinyMCE
	 */

	/**
	 * Registers all TinyMCE editors by their IDs. This method listens for newly added editors
	 * and processes all existing editors if available. If TinyMCE is not found, logs a message.
	 *
	 * @return {void} This method does not return a value.
	 */
	registerEditors() {
		const tinymce = window.tinymce || null;
		if (tinymce) {

			tinymce.onAddEditor.add((manager, editor) => this.registerEditor(editor.id));

			if (tinymce.editors.length) {
				tinymce.editors.forEach((editor) => this.registerEditor(editor.id))
			}

		} else {
			utils.odb(`tinymce not found`, this);
		}
	}

	registerEditor(editorID) {
		if (this.isRegisteredEditor(editorID)) return;
		utils.odb(`Editor "${editorID}" registered`, this);

		let Editor;
		Editor = tinymce.get(editorID);
		if (!Editor) return;

		this.Editors[editorID] = Editor;

		Editor.onInit.add(function() {
			utils.odb(`> Editor "${editorID}" initialised`, this);
		})
	}

	isRegisteredEditor(editorID) {
		return Object.hasOwn(this.Editors, editorID);
	}

	//noinspection JSUnusedGlobalSymbols
	hideEditor(editorID) {
		this.Editors[editorID].hide();
	}

	//noinspection JSUnusedGlobalSymbols
	editorFeedback(editorID, feedback = '') {
		let displayId = `editor-${editorID}-feedback`;
		const display = document.getElementById(displayId);
		if (display) {
			utils.clearTimer(displayId);

			if (feedback && feedback.length) {
				//display.html( feedback );

				utils.addTimer(() => {
					//display.html( '' );
				}, 3000, displayId);
			} else {
				//display.html( '' );
			}
		}
	}

}